import { ArrowLeft, Gauge } from 'lucide-react'
import type React from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAthletes } from '../../contexts/AthletesContext'
import { useAuth } from '../../contexts/AuthContext'
import { useRole } from '../../contexts/RoleContext'
import { FeatureFlagKey, useFeatureFlags } from '../../hooks/useFeatureFlags'
import { theme } from '../../theme/theme'
import { getAvatarUrl } from '../../utils/avatarUtils'
import { RaceResultsTable } from '../cards/RaceResultsTable'
import { TestResultsTable } from '../cards/TestResultsTable'
import { WeeklyCalendarCard } from '../cards/WeeklyCalendarCard'
import { ActionButton } from '../common/ActionButton'
import { Button } from '../common/Button'
import { ConnectDevice } from '../common/ConnectDevice'
import { DiagnosticsPage } from '../diagnostics/DiagnosticsPage'

const DashboardContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 0;
`

const AthleteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  padding: ${theme.spacing.lg} ${theme.spacing.lg} 0;

  @media (max-width: 640px) {
    padding: ${theme.spacing.md} ${theme.spacing.sm} 0;
  }
`

const AthleteAvatar = styled.div<{ $athleteId?: string; $isActive?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  overflow: hidden;
  flex-shrink: 0;
  contain: layout;
  view-transition-name: ${({ $athleteId, $isActive }) => ($athleteId && $isActive ? `athlete-avatar-${$athleteId}` : 'none')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-left: auto;
`

const AthleteName = styled.h1<{ $athleteId?: string; $isActive?: boolean }>`
  font-size: ${({ theme }) => theme.typography.xxl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  contain: layout;
  view-transition-name: ${({ $athleteId, $isActive }) => ($athleteId && $isActive ? `athlete-name-${$athleteId}` : 'none')};

  @media (max-width: 640px) {
    font-size: ${theme.typography.xl};
    margin: 0 0 ${theme.spacing.sm} 0;
  }
`

const TabContent = styled.div`
  padding: ${theme.spacing.lg};

  @media (max-width: 640px) {
    padding: ${theme.spacing.sm};
  }
`

const CalendarSection = styled.div`
  width: 100%;

  @media (min-width: 1024px) {
    min-width: 900px;
  }
`

interface AthleteDetailsProps {
  athleteId?: string
}

type TabType = 'calendar' | 'recovery' | 'physiology' | 'testing' | 'races' | 'diagnostics' | 'connect'

export const AthleteDetails: React.FC<AthleteDetailsProps> = ({ athleteId }) => {
  const { user } = useAuth()
  const { currentRole, loading: roleLoading } = useRole()
  const { enabledFeatures, loading: featuresLoading } = useFeatureFlags()
  const { athletes } = useAthletes()
  const { id: routeAthleteId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Check if user is athlete and which features are enabled
  const isAthleteView = currentRole === 'athlete'
  const isCoachView = currentRole === 'coach'
  const hasCalendar = enabledFeatures.some((f) => f.flag_key === FeatureFlagKey.AthleteCalendar)
  const hasTesting = enabledFeatures.some((f) => f.flag_key === FeatureFlagKey.AthleteTesting)
  const hasRaces = enabledFeatures.some((f) => f.flag_key === FeatureFlagKey.AthleteRaces)

  // Determine if user has any available tabs
  const hasAnyTabs = isAthleteView ? hasCalendar || hasTesting || hasRaces : true

  // Get tab from URL or default to first available tab or 'connect' if no tabs
  let defaultTab: TabType = 'calendar'
  if (isAthleteView && !hasAnyTabs) {
    defaultTab = 'connect'
  } else if (isAthleteView) {
    if (hasCalendar) defaultTab = 'calendar'
    else if (hasTesting) defaultTab = 'testing'
    else if (hasRaces) defaultTab = 'races'
  }

  const currentTab = (searchParams.get('tab') as TabType) || defaultTab

  // Determine which athleteId to use: prop > route param > authenticated user's ID
  const effectiveAthleteId = athleteId || routeAthleteId || user?.id

  // Find the athlete from the coach's athletes list
  const currentAthlete =
    isCoachView && effectiveAthleteId ? athletes.find((a) => a.id === effectiveAthleteId) : undefined

  const handleBack = () => {
    // Use View Transition API for smooth back navigation
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        flushSync(() => {
          navigate('/athletes')
        })
      })
    } else {
      navigate('/athletes')
    }
  }

  const handleEditThresholds = () => {
    navigate(`/athletes/${effectiveAthleteId}/thresholds`)
  }

  if (!effectiveAthleteId) {
    return <div>AthleteID missing</div>
  }

  // Show loading while feature flags are loading for athletes
  if (isAthleteView && featuresLoading) {
    return <div>Loading features...</div>
  }

  // Wait for role to load or if currentRole is not set
  if (roleLoading || !currentRole) {
    return <div>Loading roles...</div>
  }

  const renderTabContent = () => {
    // Show ConnectDevice if athlete has no available tabs
    if (isAthleteView && !hasAnyTabs) {
      return <ConnectDevice />
    }

    switch (currentTab) {
      /*
      case 'physiology':
        return (
          <DashboardGrid>
            <div className="dashboard-card">
              <PhysiologyCard data={displayAthlete.physiology} />
            </div>
          </DashboardGrid>
        );

      case 'recovery':
        return (
          <DashboardGrid>
            <div className="dashboard-card">
              <SleepCard data={displayAthlete.sleep} />
            </div>
            <div className="dashboard-card">
              <RecoveryCard data={displayAthlete.recovery} />
            </div>
          </DashboardGrid>
        );
      */

      case 'testing':
        // Only show if testing is enabled for athletes, or if coach view
        if (isAthleteView && !hasTesting) return <ConnectDevice />
        return <TestResultsTable athleteId={effectiveAthleteId} />

      case 'races':
        // Only show if races is enabled for athletes, or if coach view
        if (isAthleteView && !hasRaces) return <ConnectDevice />
        return <RaceResultsTable athleteId={effectiveAthleteId} />

      case 'diagnostics':
        return <DiagnosticsPage athleteId={effectiveAthleteId} />

      case 'calendar':
        // Only show if calendar is enabled for athletes, or if coach view
        if (isAthleteView && !hasCalendar) return <ConnectDevice />
        return (
          <>
            <CalendarSection>
              <WeeklyCalendarCard athleteId={effectiveAthleteId} />
            </CalendarSection>
          </>
        )

      case 'connect':
        return <ConnectDevice />

      default:
        return <ConnectDevice />
    }
  }

  // Get avatar info
  const avatarUrl = currentAthlete?.avatar ? getAvatarUrl(currentAthlete.avatar) : null
  const initials = currentAthlete
    ? currentAthlete.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
    : ''

  return (
    <DashboardContainer>
      {isCoachView && currentAthlete && (
        <AthleteHeader>
          <ActionButton variant="default" onClick={handleBack}>
            <ArrowLeft size={16} />
          </ActionButton>
          <AthleteAvatar $athleteId={currentAthlete.id} $isActive={true}>
            {avatarUrl ? <img src={avatarUrl} alt={currentAthlete.name} /> : initials}
          </AthleteAvatar>
          <AthleteName $athleteId={currentAthlete.id} $isActive={true}>{currentAthlete.name}</AthleteName>
          <HeaderActions>
            <Button variant="secondary" size="md" onClick={handleEditThresholds}>
              <Gauge size={16} />
              Thresholds
            </Button>
          </HeaderActions>
        </AthleteHeader>
      )}
      <TabContent>{renderTabContent()}</TabContent>
    </DashboardContainer>
  )
}
